// src/pages/ChatBot/DocumentsPanel.tsx
//
// Documents tab of the Manage Chat console. Carries the upload/download/
// delete feature set of the legacy ManageContent page (PDF upload with
// overwrite handling, sortable document table) redesigned onto the dark
// admin surface, with browser-native confirm() popups replaced by the shared
// DeleteConfirmModal.
//
// Document access is scoped to the tenant's RBAC roles (multi-select per
// document) instead of the legacy user/manager access-level toggle. The role
// list is fetched once on mount from /roles — the same call the users page
// makes. Roles are chosen per file in the upload staging table before upload,
// persisted by the backend (ChatBotDocuments/ blob metadata + ChatBotEmbeddings
// chunks), and edited afterwards via the per-row Access modal. An empty role
// selection means the document is open to every chatbot user.
//
// All backend calls target the /chatbot/* routes (the new chatbot's isolated
// endpoints), except /roles which is a shared platform endpoint.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';
import DocumentAccessModal, { OrgRole } from './DocumentAccessModal';
import DocumentInspectModal, { DocumentInspectTarget } from './DocumentInspectModal';
import { coversAllRoles } from './document-roles';
import Tag from './shared/Tag';
import './DocumentsPanel.css';

type UploadedDoc = {
  fileName: string;
  blobName?: string;
  uploadedBy?: string;
  uploadTime?: string;
  roleIDs?: string[];
  hideActions?: boolean;
};

type SortColumn = 'filename' | 'uploaded_by' | 'upload_time';

type ConfirmState = {
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor: string;
  onConfirm: () => void;
  onCancel?: () => void;
};

const BRAND_GREEN = '#218838';
const MAX_VISIBLE_ROLE_CHIPS = 3;

export default function DocumentsPanel() {
  const [docs, setDocs] = useState<UploadedDoc[]>([]);
  const [status, setStatus] = useState('');
  const [sortBy, setSortBy] = useState<SortColumn>('filename');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Files chosen but not yet uploaded, and the roles selected for each (keyed by
  // file name). Both are cleared once the upload succeeds or is cancelled.
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [uploadRoleScopes, setUploadRoleScopes] = useState<Record<string, string[]>>({});
  const [stagedAccessEditor, setStagedAccessEditor] = useState<string | null>(null);
  // True while an upload request is in flight — drives the spinner and disables
  // every other control so no conflicting mutation can be triggered mid-upload.
  const [isUploading, setIsUploading] = useState(false);

  // Tenant RBAC roles — null until the one-time fetch resolves.
  const [roles, setRoles] = useState<OrgRole[] | null>(null);
  const [rolesFailed, setRolesFailed] = useState(false);

  // Role scoping per uploaded document, keyed by fileName. Seeded from the
  // backend on load and updated after edits. Empty/missing means unrestricted.
  const [docRoleScopes, setDocRoleScopes] = useState<Record<string, string[]>>({});
  const [accessEditorDoc, setAccessEditorDoc] = useState<string | null>(null);

  // The document whose Inspect modal is open (blobName + fileName), or null.
  const [inspectDoc, setInspectDoc] = useState<DocumentInspectTarget | null>(null);

  const toggleSort = (col: SortColumn) => {
    if (isUploading) return;
    if (sortBy === col) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setOrder('asc');
    }
  };

  const sortIndicator = (col: SortColumn) =>
    sortBy === col ? (order === 'asc' ? ' ▲' : ' ▼') : '';

  // Fetch once (and after mutations) — re-sorting is done locally so column
  // clicks don't round-trip to the backend and flash the loading status.
  // Seeds docRoleScopes from each document's persisted roleIDs so the access
  // tags survive a refresh.
  const fetchDocs = useCallback(async () => {
    setStatus('Loading documents…');
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/chatbot/uploaded_docs?sort_by=filename&order=asc`,
        { credentials: 'include', method: 'GET' },
      );
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const { uploaded_docs } = await res.json();
      const list: UploadedDoc[] = uploaded_docs || [];
      setDocs(list);
      const scopes: Record<string, string[]> = {};
      list.forEach((d) => {
        if (d.fileName) scopes[d.fileName] = d.roleIDs ?? [];
      });
      setDocRoleScopes(scopes);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setDocs([]);
    } finally {
      setStatus('');
    }
  }, []);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  // Fetch the tenant's RBAC roles once on load — same call and shape as the
  // users page ({ org: [{ id, name, ... }] }). This is a shared platform
  // endpoint, so it stays un-prefixed (not under /chatbot).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/roles?scope=all`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
        const data = await res.json();
        if (!cancelled) setRoles(data.org ?? []);
      } catch (err) {
        console.error('Failed to fetch roles:', err);
        if (!cancelled) {
          setRoles([]);
          setRolesFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // System roles pinned first, then alphabetical.
  const sortedRoles = useMemo(() => {
    if (!roles) return [];
    return [...roles].sort(
      (a, b) =>
        Number(b.isSystem ?? false) - Number(a.isSystem ?? false) ||
        a.name.localeCompare(b.name),
    );
  }, [roles]);

  const sortedDocs = useMemo(() => {
    const compare = (a: UploadedDoc, b: UploadedDoc): number => {
      if (sortBy === 'upload_time') {
        return (
          new Date(a.uploadTime || 0).getTime() -
          new Date(b.uploadTime || 0).getTime()
        );
      }
      const left = sortBy === 'uploaded_by' ? a.uploadedBy : a.fileName;
      const right = sortBy === 'uploaded_by' ? b.uploadedBy : b.fileName;
      return (left || '').localeCompare(right || '', undefined, {
        sensitivity: 'base',
      });
    };
    const sorted = [...docs].sort(compare);
    return order === 'asc' ? sorted : sorted.reverse();
  }, [docs, sortBy, order]);

  const uploadFiles = async (overwrite: boolean) => {
    if (!stagedFiles.length) return;

    setIsUploading(true);
    setStatus('Uploading…');
    const form = new FormData();
    stagedFiles.forEach((f) => form.append('documents', f));

    // Per-file role selection: { [fileName]: roleID[] }. Files with no chosen
    // role are sent as an empty array (open to everyone).
    const roleScopes: Record<string, string[]> = {};
    stagedFiles.forEach((f) => {
      roleScopes[f.name] = uploadRoleScopes[f.name] ?? [];
    });
    form.append('roleScopes', JSON.stringify(roleScopes));

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/chatbot/process_upload${overwrite ? '?overwrite=true' : ''}`,
        { method: 'POST', credentials: 'include', body: form },
      );

      if (res.status === 409 && !overwrite) {
        setConfirm({
          title: 'Overwrite Document',
          message: 'A file already exists with that name. Overwrite?',
          confirmLabel: 'Overwrite',
          confirmColor: BRAND_GREEN,
          onConfirm: () => {
            setConfirm(null);
            uploadFiles(true);
          },
          onCancel: () => setStatus('Upload cancelled by user'),
        });
        return;
      }
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

      await fetchDocs();
      clearSelection();
      setStatus(overwrite ? 'File overwritten successfully' : 'Upload successful');
    } catch {
      setStatus('Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = (filename: string) => {
    setConfirm({
      title: 'Delete Document',
      message: `Delete "${filename}"?`,
      confirmLabel: 'Delete',
      confirmColor: '#c62828',
      onConfirm: async () => {
        setConfirm(null);
        setStatus('Deleting…');
        try {
          const res = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/chatbot/uploaded_docs?filename=${encodeURIComponent(filename)}`,
            { method: 'DELETE', credentials: 'include' },
          );
          if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
          await fetchDocs();
          setStatus('Deleted');
        } catch {
          setStatus('Delete failed');
        }
      },
    });
  };

  // Persist a role-scope change for an already-uploaded document. Optimistic:
  // apply locally, then revert on failure.
  const handleSaveScope = async (fileName: string, roleIDs: string[]) => {
    setAccessEditorDoc(null);
    const previous = docRoleScopes[fileName] ?? [];
    setDocRoleScopes((s) => ({ ...s, [fileName]: roleIDs }));
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/chatbot/update_metadata`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: fileName, roleIDs }),
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = await res.json();
      // Reflect the server's validated set (it drops any unknown role IDs).
      if (Array.isArray(data.roleIDs)) {
        setDocRoleScopes((s) => ({ ...s, [fileName]: data.roleIDs }));
      }
      setStatus('Access updated');
    } catch {
      setDocRoleScopes((s) => ({ ...s, [fileName]: previous }));
      setStatus('Failed to update access');
    }
  };

  const clearSelection = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setStagedFiles([]);
    setUploadRoleScopes({});
    setStagedAccessEditor(null);
  };

  const roleName = (roleID: string) =>
    roles?.find((r) => r.id === roleID)?.name ?? 'Deleted role';

  // Renders the role chips for a given role-ID set: "Everyone" when open or when
  // every tenant role is selected, otherwise up to MAX_VISIBLE_ROLE_CHIPS named
  // chips with a "+N" overflow.
  const renderRoleChips = (roleIDs: string[] | undefined) => {
    const scoped = roleIDs ?? [];
    const coversAll = coversAllRoles(scoped, roles);

    if (scoped.length === 0 || coversAll) {
      return (
        <Tag
          tone="open"
          title={coversAll ? 'All roles selected — open to everyone' : 'No role restriction'}
        >
          Everyone
        </Tag>
      );
    }

    const visible = scoped.slice(0, MAX_VISIBLE_ROLE_CHIPS);
    const overflow = scoped.slice(MAX_VISIBLE_ROLE_CHIPS);
    return (
      <>
        {visible.map((roleID) => (
          <Tag key={roleID} tone="brass">
            {roleName(roleID)}
          </Tag>
        ))}
        {overflow.length > 0 && (
          <span className="mcd-tag-more" title={overflow.map(roleName).join(', ')}>
            +{overflow.length}
          </span>
        )}
      </>
    );
  };

  return (
    <div className="mcd-panel">
      {/* Upload card — the control bar (file picker + upload buttons + status)
          and, once files are staged, the per-file role table live in one banner. */}
      <div className={`mcd-upload-card${stagedFiles.length > 0 ? ' staged' : ''}`}>
        <div className="mcd-upload-bar">
          <input
            id="mcdFileInput"
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            multiple
            disabled={isUploading}
            onChange={(e) => setStagedFiles(Array.from(e.target.files ?? []))}
            style={{ display: 'none' }}
          />
          {isUploading ? (
            <span className="mcd-uploading" role="status" aria-live="polite">
              <span className="mcd-spinner" aria-hidden="true" />
              Uploading {stagedFiles.length} PDF(s)…
            </span>
          ) : (
            <>
              <label htmlFor="mcdFileInput" className="mcd-choose-btn">
                Choose PDF(s)
              </label>
              {stagedFiles.length > 0 && (
                <>
                  <button
                    type="button"
                    className="mcd-upload-btn"
                    onClick={() => uploadFiles(false)}
                  >
                    Upload {stagedFiles.length} PDF(s)
                  </button>
                  <button
                    type="button"
                    className="mcd-cancel-btn"
                    onClick={clearSelection}
                  >
                    Cancel
                  </button>
                </>
              )}
              {status && <span className="mcd-status">{status}</span>}
            </>
          )}
        </div>

        {/* Per-file role table — one row per chosen file, shown inside the same
            card while files are staged (before upload). */}
        {stagedFiles.length > 0 && (
          <div className="mcd-staging-body">
            <table className="mcd-table">
              <thead>
                <tr>
                  <th>File to upload</th>
                  <th className="mcd-th-access">Access</th>
                  <th className="mcd-th-actions">Roles</th>
                </tr>
              </thead>
              <tbody>
                {stagedFiles.map((f) => (
                  <tr key={f.name}>
                    <td>{f.name}</td>
                    <td className="mcd-access-cell">
                      {renderRoleChips(uploadRoleScopes[f.name])}
                    </td>
                    <td className="mcd-actions">
                      <button
                        type="button"
                        className="mcd-action-btn access"
                        title={
                          rolesFailed
                            ? 'Role list unavailable — reload the page to retry'
                            : 'Set role access'
                        }
                        disabled={roles === null || rolesFailed || isUploading}
                        onClick={() => setStagedAccessEditor(f.name)}
                      >
                        <span className="icon">A</span>
                        <span className="label">Set roles</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Uploaded document table */}
      {docs.length === 0 ? (
        <div className="mcd-empty">No documents uploaded yet.</div>
      ) : (
        <div className="mcd-table-card">
          <table className="mcd-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort('filename')}>
                  Filename{sortIndicator('filename')}
                </th>
                <th onClick={() => toggleSort('uploaded_by')}>
                  Last Uploaded By{sortIndicator('uploaded_by')}
                </th>
                <th onClick={() => toggleSort('upload_time')}>
                  Last Upload Time (ET){sortIndicator('upload_time')}
                </th>
                <th className="mcd-th-access">Access</th>
                <th className="mcd-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedDocs.map((doc) => (
                <tr key={doc.fileName}>
                  <td>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/chatbot/download/${encodeURIComponent(doc.fileName)}`}
                      className="mcd-filename-link"
                      tabIndex={0}
                    >
                      {doc.fileName}
                    </a>
                  </td>
                  <td>{doc.uploadedBy || '—'}</td>
                  <td>
                    {doc.uploadTime
                      ? new Date(doc.uploadTime).toLocaleString('en-US', {
                          timeZone: 'America/New_York',
                        })
                      : '—'}
                  </td>
                  <td className="mcd-access-cell">
                    {renderRoleChips(docRoleScopes[doc.fileName] ?? doc.roleIDs)}
                  </td>
                  <td className="mcd-actions">
                    {doc.hideActions === true ? (
                      <span>---</span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="mcd-action-btn inspect"
                          title="Inspect"
                          aria-label="Inspect"
                          disabled={!doc.blobName}
                          onClick={() =>
                            doc.blobName &&
                            setInspectDoc({ blobName: doc.blobName, fileName: doc.fileName })
                          }
                        >
                          <span className="icon">👁</span>
                          <span className="label">Inspect</span>
                        </button>
                        <button
                          type="button"
                          className="mcd-action-btn delete"
                          title="Delete"
                          disabled={isUploading}
                          onClick={() => handleDelete(doc.fileName)}
                        >
                          <span className="icon">✕</span>
                          <span className="label">Delete</span>
                        </button>
                        <button
                          type="button"
                          className="mcd-action-btn access"
                          title={
                            rolesFailed
                              ? 'Role list unavailable — reload the page to retry'
                              : 'Edit role access'
                          }
                          disabled={roles === null || rolesFailed || isUploading}
                          onClick={() => setAccessEditorDoc(doc.fileName)}
                        >
                          <span className="icon">A</span>
                          <span className="label">Access</span>
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={!!confirm}
        onClose={() => {
          confirm?.onCancel?.();
          setConfirm(null);
        }}
        onConfirm={confirm?.onConfirm || (() => {})}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
      />

      {/* Access editor for a staged (not-yet-uploaded) file */}
      {stagedAccessEditor !== null && (
        <DocumentAccessModal
          fileName={stagedAccessEditor}
          roles={sortedRoles}
          initialRoleIDs={uploadRoleScopes[stagedAccessEditor] ?? []}
          onSave={(roleIDs) => {
            setUploadRoleScopes((s) => ({ ...s, [stagedAccessEditor]: roleIDs }));
            setStagedAccessEditor(null);
          }}
          onClose={() => setStagedAccessEditor(null)}
        />
      )}

      {/* Access editor for an already-uploaded document */}
      {accessEditorDoc !== null && (
        <DocumentAccessModal
          fileName={accessEditorDoc}
          roles={sortedRoles}
          initialRoleIDs={docRoleScopes[accessEditorDoc] ?? []}
          onSave={(roleIDs) => handleSaveScope(accessEditorDoc, roleIDs)}
          onClose={() => setAccessEditorDoc(null)}
        />
      )}

      {/* Read-only document inspector (stats + PDF preview) */}
      <DocumentInspectModal
        doc={inspectDoc}
        roles={roles}
        onClose={() => setInspectDoc(null)}
      />
    </div>
  );
}
